import { Injectable, inject } from '@angular/core';
import { PostgrestError } from '@supabase/supabase-js';
import { Plan } from '../models/subscription.model';
import { ContentSettings } from '../models/settings.model';
import { SupabaseService } from './supabase.service';

const PLANS_CACHE_KEY = 'correctify-plans-cache-v1';
const PLANS_CACHE_MAX_AGE_MS = 5 * 60 * 1000;
const LEGAL_LAST_UPDATED = '2 de abril de 2026';

const TERMS_OF_USE_DEFAULT = `TERMOS DE USO - CORRECTIFY
Última atualização: ${LEGAL_LAST_UPDATED}

1. Aceitação
Ao criar conta, acessar ou usar o Correctify, você concorda com estes Termos de Uso e com a Política de Privacidade.

2. Objeto do serviço
O Correctify oferece recursos de apoio ao estudo de redação, incluindo análises automatizadas, histórico, recomendações e funcionalidades de assinatura.
O serviço tem finalidade educacional e não garante aprovação em concursos, exames ou processos seletivos.

3. Cadastro e acesso
Você deve fornecer dados verdadeiros e atualizados no cadastro.
Você é responsável por manter a confidencialidade da sua senha e por toda atividade realizada na sua conta.
O uso da conta por terceiros sem autorização é proibido.

4. Uso adequado da plataforma
Você concorda em não:
- usar o serviço para atividades ilegais;
- tentar explorar vulnerabilidades, engenharia reversa ou acesso indevido;
- inserir conteúdo que viole direitos de terceiros, leis aplicáveis ou normas de convivência.
O Correctify pode limitar, suspender ou encerrar contas em caso de uso abusivo, fraude ou violação destes Termos.

5. Planos, créditos e cobrança
Planos pagos e créditos são cobrados conforme valores e condições exibidos no momento da compra.
Prazos, renovação, cancelamento e eventual estorno seguem as regras comerciais informadas no checkout e as políticas do provedor de pagamento.
Créditos promocionais, gratuitos ou de teste podem ter regras específicas de uso.

6. Disponibilidade e limitações
O Correctify busca alta disponibilidade, mas pode sofrer indisponibilidades temporárias por manutenção, falhas de terceiros, rede ou força maior.
Podemos alterar, evoluir ou descontinuar funcionalidades, com comunicação razoável quando aplicável.

7. Propriedade intelectual
Marca, software, interface, código, design e materiais do Correctify são protegidos por propriedade intelectual.
Não é permitido copiar, distribuir, vender, licenciar ou explorar comercialmente o serviço sem autorização prévia.

8. Conteúdo enviado pelo usuário
Você permanece titular do conteúdo que envia.
Ao usar a plataforma, você concede licença necessária para processar esse conteúdo com a finalidade de entregar as funcionalidades contratadas.

9. Limitação de responsabilidade
Na máxima extensão permitida por lei, o Correctify não se responsabiliza por danos indiretos, lucros cessantes ou perdas decorrentes de uso indevido, indisponibilidade ou decisões tomadas com base nos resultados da plataforma.

10. Privacidade e proteção de dados
O tratamento de dados pessoais ocorre conforme a Política de Privacidade e a legislação aplicável, incluindo a LGPD.

11. Alterações destes Termos
Podemos atualizar estes Termos periodicamente.
Versões atualizadas serão publicadas na plataforma com data de revisão.

12. Contato
Em caso de dúvidas, suporte, requisições legais ou LGPD:
contato@usecorrectify.com`;

const PRIVACY_POLICY_DEFAULT = `POLÍTICA DE PRIVACIDADE - CORRECTIFY
Última atualização: ${LEGAL_LAST_UPDATED}

1. Escopo
Esta Política explica como o Correctify coleta, usa, compartilha e protege dados pessoais de usuários da plataforma.

2. Dados coletados
Podemos tratar:
- dados cadastrais: nome, email e identificadores de conta;
- dados de uso: eventos de navegação, interações e dados técnicos;
- dados de conteúdo: textos enviados para correção e histórico de resultados;
- dados de pagamento: informações necessárias para processar assinaturas e créditos por meio de provedores parceiros.
Não armazenamos dados de cartão completos no Correctify.

3. Finalidades de tratamento
Tratamos dados para:
- autenticar usuários e manter segurança da conta;
- prestar funcionalidades de correção e evolução de redação;
- processar assinaturas, créditos e conciliação de pagamentos;
- cumprir obrigações legais e regulatórias;
- prevenir fraude, abuso e incidentes de segurança;
- melhorar produto, performance e experiência do usuário.

4. Bases legais (LGPD)
As bases legais podem incluir:
- execução de contrato;
- cumprimento de obrigação legal;
- exercício regular de direitos;
- legítimo interesse;
- consentimento, quando exigido.

5. Compartilhamento de dados
Podemos compartilhar dados com operadores e provedores essenciais para o funcionamento do serviço, como:
- infraestrutura e banco de dados;
- processamento de pagamento;
- processamento de IA.
Esses parceiros tratam dados conforme instrução contratual e requisitos de segurança.

6. Retenção e descarte
Mantemos dados pelo período necessário para cumprir finalidades legítimas e obrigações legais.
Quando cabível, dados são anonimizados ou excluídos de forma segura.

7. Direitos do titular
Nos termos da LGPD, você pode solicitar:
- confirmação de tratamento;
- acesso, correção e portabilidade;
- anonimização, bloqueio ou eliminação;
- informação sobre compartilhamento;
- revisão de decisões automatizadas, quando aplicável;
- revogação de consentimento, quando aplicável.
Para exercer direitos: contato@usecorrectify.com

8. Segurança
Adotamos medidas técnicas e organizacionais para proteger dados contra acesso não autorizado, perda, alteração ou divulgação indevida.
Nenhum sistema é totalmente imune a riscos.

9. Cookies e tecnologias similares
Podemos usar recursos técnicos para autenticação, sessão, segurança, medição de uso e melhoria de desempenho.

10. Transferência internacional
Alguns provedores podem processar dados fora do Brasil.
Nesses casos, adotamos salvaguardas adequadas conforme a legislação aplicável.

11. Atualizações desta Política
Podemos atualizar esta Política periodicamente.
A versão vigente sempre estará disponível na plataforma com data de revisão.

12. Contato e encarregado
Para dúvidas de privacidade, exercício de direitos e temas LGPD:
contato@usecorrectify.com`;

@Injectable({
  providedIn: 'root'
})
export class PublicDataService {
  private supabase = inject(SupabaseService).client;
  private readonly requestTimeoutMs = 20000;
  private plansCache: Plan[] = [];

  invalidatePlansCache(): void {
    this.plansCache = [];
    if (typeof window === 'undefined') {
      return;
    }
    try {
      localStorage.removeItem(PLANS_CACHE_KEY);
    } catch {
      // no-op
    }
  }

  watchPlansUpdates(onChange: () => void): () => void {
    const channel = this.supabase
      .channel(`public:plans:${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'plans' },
        () => {
          this.invalidatePlansCache();
          onChange();
        }
      )
      .subscribe();

    return () => {
      void this.supabase.removeChannel(channel);
    };
  }

  private handleError(_error: PostgrestError | Error | null, _context: string) {
    // Intencionalmente silencioso para nao poluir console do usuario em falhas transitórias.
  }

  private async fetchPlansOnce(timeoutMs = this.requestTimeoutMs): Promise<Plan[]> {
    const queryPromise = this.supabase
      .from('plans')
      .select('*')
      .order('price', { ascending: true });

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Timeout ao carregar planos apos ${timeoutMs}ms.`));
      }, timeoutMs);
    });

    const { data, error } = await Promise.race([queryPromise, timeoutPromise]);
    if (error) {
      throw error;
    }

    return (data ?? []) as Plan[];
  }

  private loadPlansFromBrowserCache(): Plan[] {
    if (typeof window === 'undefined') {
      return [];
    }

    try {
      const raw = localStorage.getItem(PLANS_CACHE_KEY);
      if (!raw) {
        return [];
      }

      const parsed = JSON.parse(raw) as { savedAt?: number; data?: Plan[] };
      if (!parsed?.savedAt || !Array.isArray(parsed.data)) {
        return [];
      }

      const isStale = Date.now() - parsed.savedAt > PLANS_CACHE_MAX_AGE_MS;
      return isStale ? [] : parsed.data;
    } catch {
      return [];
    }
  }

  private isLikelyPlaceholderLegalText(text: string): boolean {
    const normalized = text
      .toLowerCase()
      .trim()
      .replace(/\s+/g, ' ');

    if (!normalized) {
      return true;
    }

    if (normalized.length < 120) {
      return true;
    }

    return (
      normalized === 'termos de servico do correctify.'
      || normalized === 'politica de privacidade do correctify.'
    );
  }

  private savePlansToBrowserCache(plans: Plan[]): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(PLANS_CACHE_KEY, JSON.stringify({
        savedAt: Date.now(),
        data: plans,
      }));
    } catch {
      // no-op
    }
  }

  async getSettingsContent(): Promise<ContentSettings> {
    const hardcodedDefaults: ContentSettings = {
      termsOfUse: TERMS_OF_USE_DEFAULT,
      privacyPolicy: PRIVACY_POLICY_DEFAULT,
      defaultWelcomeMessage: 'Bem-vindo ao Correctify! Envie sua primeira redação.',
    };

    try {
      const { data, error } = await this.supabase.rpc('get_content_settings');
      if (error) throw error;
      if (!data) return hardcodedDefaults;

      const fetchedTerms = String(data.termsOfUse ?? '').trim();
      const fetchedPrivacy = String(data.privacyPolicy ?? '').trim();

      return {
        termsOfUse: this.isLikelyPlaceholderLegalText(fetchedTerms)
          ? hardcodedDefaults.termsOfUse
          : fetchedTerms,
        privacyPolicy: this.isLikelyPlaceholderLegalText(fetchedPrivacy)
          ? hardcodedDefaults.privacyPolicy
          : fetchedPrivacy,
        defaultWelcomeMessage: data.defaultWelcomeMessage ?? hardcodedDefaults.defaultWelcomeMessage,
      };
    } catch (error: any) {
      this.handleError(error, 'getSettingsContent');
      return hardcodedDefaults;
    }
  }

  async getPlans(): Promise<Plan[]> {
    try {
      const plans = await this.fetchPlansOnce();
      if (plans.length > 0) {
        this.plansCache = plans;
        this.savePlansToBrowserCache(plans);
        return plans;
      }

      this.logSetupWarning();
      if (this.plansCache.length > 0) {
        return this.plansCache;
      }
      throw new Error('Nenhum plano ativo foi encontrado no momento.');
    } catch (error: any) {
      this.handleError(error, 'getPlans');

      const localCache = this.loadPlansFromBrowserCache();
      if (localCache.length > 0) {
        this.plansCache = localCache;
        return localCache;
      }

      try {
        const retryPlans = await this.fetchPlansOnce(Math.floor(this.requestTimeoutMs * 0.75));
        if (retryPlans.length > 0) {
          this.plansCache = retryPlans;
          this.savePlansToBrowserCache(retryPlans);
          return retryPlans;
        }
      } catch (retryError: any) {
        this.handleError(retryError, 'getPlans(retry)');
      }

      this.logSetupWarning();
      if (this.plansCache.length > 0) {
        return this.plansCache;
      }
      throw new Error('Não foi possível carregar os planos no momento.');
    }
  }

  private logSetupWarning() {
    // Intencionalmente silencioso para nao poluir console do usuario.
  }
}
